import {
    Component,
    ComponentInterface,
    Element,
    h,
    Host,
    Prop,
    State,
    Watch,
    Event,
    EventEmitter,
} from "@stencil/core";
import {
    AutoSuggestClickType,
    QuickLink,
    QuickLinksApiResponse,
} from "./types";
import { track } from "../../utils/tracking";

/** static base className */
const baseClass = "ux-iws-autosuggest";

@Component({
    tag: "ux-iws-autosuggest",
    styleUrl: "autosuggest.scss",
})
export class Autosuggest implements ComponentInterface {
    /** the auto-suggest dom element */
    @Element() domNode: HTMLUxIwsAutosuggestElement;

    /** Value of the search input field */
    @Prop() searchString: string = "";
    /** Endpoint for autocomplete API */
    @Prop() autosuggestEndpoint: string = "";
    /** Endpoint for quickLinks API */
    @Prop() quickLinksEndpoint: string = "";
    /** Label for search results title in autosuggest */
    @Prop() resultsLabel: string = "";
    /** Label for QuickLinks */
    @Prop() quickLinksLabel: string = "";
    /** Label for Curated Search */
    @Prop() curatedSearchLabel: string = "";
    /** Array of autocomplete suggestions */
    @State() autocompleteSuggestions: string[] = [];
    /** Additional Links to be displayed in the autosuggest */
    @State() quickLinks: QuickLink[] = [];
    /** Array of curated search terms */
    @State() curatedSearchTerms: string[] = [];
    /** Array of autocomplete suggestions buttons */
    @State() suggestionsBtnList: string[] = [];
    /** Event emitted when the user clicks autosuggestion; triggers search for the chosen autosuggestion */
    @Event({
        eventName: "uxAutosuggestClick",
    })
    autosuggestClick: EventEmitter<{
        suggestion: string;
        type: AutoSuggestClickType;
    }>;

    @Watch("searchString")
    private getAutocompleteSuggestions() {
        if (this.autosuggestEndpoint === "") return;
        if (this.searchString.length < 3) {
            this.autocompleteSuggestions = [];
            return;
        }
        const url = `${this.autosuggestEndpoint}${
            this.autosuggestEndpoint.includes("?") ? "&" : "?"
        }q=${this.searchString}`;
        fetch(url)
            .then((response) => response.json())
            .then((data) => {
                this.autocompleteSuggestions = data;
            })
            .catch((error) => {
                console.error(error);
                this.autocompleteSuggestions = [];
            });
    }

    @Watch("quickLinksEndpoint")
    private getQuickLinksData() {
        if (this.quickLinksEndpoint === "") {
            this.quickLinks = [];
            this.curatedSearchTerms = [];
            return;
        }
        fetch(this.quickLinksEndpoint)
            .then((response) => response.json())
            .then((data: QuickLinksApiResponse) => {
                this.quickLinks = data.quickLinks || [];
                this.curatedSearchTerms = data.curatedSearchTerms || [];
            })
            .catch((error) => {
                console.log(error);
            });
    }

    componentWillLoad() {
        this.getAutocompleteSuggestions();
        this.getQuickLinksData();
    }

    /** Highlight the searched part of an item of the autocomplete suggestions */
    private highlightFoundPart(text: string) {
        const searchString = this.searchString.replace(
            /[-/\\^$*+?.()|[\]{}]/g,
            "\\$&"
        );
        const regex = new RegExp(`(${searchString})`, "gi");
        return text.replace(regex, `<span>$1</span>`);
    }

    render() {
        if (!this.autocompleteSuggestions.length) return;
        const showExtras =
            this.quickLinks.length + this.curatedSearchTerms.length > 0;
        return (
            <Host class={`${baseClass}`}>
                <div class={`${baseClass}__inner`}>
                    <div class={`${baseClass}__layout`}>
                        <div class={`${baseClass}__suggestions`}>
                            <div class={`${baseClass}__title`}>
                                {this.resultsLabel}
                            </div>
                            <div class={`${baseClass}__results`}>
                                {this.autocompleteSuggestions.map(
                                    (suggestion) => (
                                        <button
                                            class={`${baseClass}__button`}
                                            key={suggestion}
                                            type="button"
                                            tabindex="0"
                                            innerHTML={this.highlightFoundPart(
                                                suggestion
                                            )}
                                            onClick={() =>
                                                this.autosuggestClick.emit({
                                                    suggestion: suggestion,
                                                    type: "suggestion",
                                                })
                                            }
                                        ></button>
                                    )
                                )}
                            </div>
                        </div>
                        {showExtras && (
                            <div class={`${baseClass}__extras`}>
                                {this.quickLinks.length > 0 && (
                                    <div class={`${baseClass}__quick-links`}>
                                        <div class={`${baseClass}__title`}>
                                            {this.quickLinksLabel}
                                        </div>
                                        <ul
                                            class={`${baseClass}__extras__list`}
                                        >
                                            {this.quickLinks.map((link) => (
                                                <li
                                                    class={`${baseClass}__quick-links-item`}
                                                    key={link.href}
                                                >
                                                    <a
                                                        class={`${baseClass}__button`}
                                                        href={link.href}
                                                        onClick={() =>
                                                            track(
                                                                "auto_suggest_quick_link_click",
                                                                {
                                                                    href: link.href,
                                                                }
                                                            )
                                                        }
                                                    >
                                                        <span>
                                                            {link.label}
                                                        </span>
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {this.curatedSearchTerms.length > 0 && (
                                    <div class={`${baseClass}__curated-search`}>
                                        <div class={`${baseClass}__title`}>
                                            {this.curatedSearchLabel}
                                        </div>
                                        <ul
                                            class={`${baseClass}__extras__list`}
                                        >
                                            {this.curatedSearchTerms.map(
                                                (term) => (
                                                    <li
                                                        class={`${baseClass}__curated-search-item`}
                                                        key={term}
                                                    >
                                                        <button
                                                            class={`${baseClass}__button`}
                                                            type="button"
                                                            onClick={() =>
                                                                this.autosuggestClick.emit(
                                                                    {
                                                                        suggestion:
                                                                            term,
                                                                        type: "curatedSearchTerm",
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            <span>{term}</span>
                                                        </button>
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Host>
        );
    }
}
